import type { ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useTranslation } from "react-i18next";
import { BookOpenCheck, X } from "lucide-react";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import type { AssistantModeDef, AssistantModeId } from "@/components/assistant/assistantModes";

/**
 * El menú de modos que abre la «@» en la caja del chat (CQ4b · PLAN §3).
 *
 * Popover anclado a la píldora del composer, hacia ARRIBA (en la hoja del
 * micrófono y sobre el teclado del teléfono no cabe hacia abajo), con cmdk para
 * la lista. El foco se QUEDA en la caja: se sigue tecleando y las flechas, Enter
 * y Escape los maneja el composer (IpmAiAssistant) con `active`/`onSelect`.
 */
export function AssistantModeMenu({
  open,
  modes,
  active,
  onActiveChange,
  onSelect,
  onClose,
  children,
}: {
  open: boolean;
  modes: AssistantModeDef[];
  active: AssistantModeId | null;
  onActiveChange: (id: AssistantModeId) => void;
  onSelect: (id: AssistantModeId) => void;
  onClose: () => void;
  /** La píldora del composer: el ancla del menú. */
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Popover open={open && modes.length > 0} onOpenChange={(o) => { if (!o) onClose(); }}>
      <PopoverPrimitive.Anchor asChild>{children}</PopoverPrimitive.Anchor>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[min(18rem,calc(100vw-2rem))] p-0"
        data-testid="assistant-mode-menu"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command value={active ?? undefined} onValueChange={(v) => onActiveChange(v as AssistantModeId)} shouldFilter={false} loop>
          <CommandList>
            <CommandGroup heading={t("ipmAssistant.mode.menuTitle", "Modos")}>
              {modes.map((m) => (
                <CommandItem
                  key={m.id}
                  value={m.id}
                  onSelect={() => onSelect(m.id)}
                  data-testid={`assistant-mode-option-${m.id}`}
                  className="min-h-[44px] gap-2.5"
                >
                  <BookOpenCheck className="h-4 w-4 shrink-0 text-[#2E84DE]" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{t(m.labelKey)}</span>
                    <span className="block text-[11px] text-muted-foreground">{t(m.hintKey)}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** El chip del modo puesto, dentro de la píldora del composer, con su × para salir. */
export function AssistantModeChip({ mode, onClear, className }: { mode: AssistantModeDef; onClear: () => void; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`assistant-mode-chip-${mode.id}`}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full border border-[#b6dded] bg-[#f4f9ff] py-1 pl-2.5 pr-1 text-xs font-semibold text-[#1d5fa8]",
        className,
      )}
    >
      <BookOpenCheck className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{t(mode.labelKey)}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={t("ipmAssistant.mode.exit", "Salir del modo")}
        data-testid={`assistant-mode-chip-close-${mode.id}`}
        className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-[#1d5fa8]/70 hover:bg-[#e3f0fc] hover:text-[#1d5fa8]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}
